// ==========================
// FILE: src/components/Belongings.js
// ==========================
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom"; // ✅ NEW: portal for the read-only View modal
import { db, storage } from "../firebase";
import { useFreeLimits } from "../hooks/useFreeLimits";
import UpgradeModal from "./UpgradeModal";
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { FiEye } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
import {
  useUserContacts,
  groupContactsForOptions,
  AddContactModal,
  useAddContactAutoSelect,
} from "../utils/contacts-ui";
import { printEntry, downloadEntry } from "../utils/printDownload";
import { useAccess } from "../access/AccessContext";
import VideoLightbox from "./ui/VideoLightbox";
import "../styles/layout.css";

// ---- helpers ----
function contactNameFromId(contacts, id) {
  return contacts.find((c) => c.id === id)?.name || "";
}
function saveTextFile(filename, text) {
  const blob = new Blob([text || ""], { type: "text/plain;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename || "belonging.txt";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 0);
}
function buildSummaryText(row, contacts) {
  const lines = [];
  lines.push("Belonging");
  lines.push(`Generated: ${new Date().toLocaleString()}`);
  lines.push("");
  lines.push(`Title: ${row.title || ""}`);
  lines.push(`Description: ${row.description || ""}`);
  lines.push(`Estimated Value: ${row.estimatedValue || ""}`);
  const assigned =
    row.contactId === "other"
      ? row.customContact || row.contactName || ""
      : row.contactName ||
        (row.contactId ? contactNameFromId(contacts, row.contactId) : "");
  lines.push(`Assign To: ${assigned}`);
  const imgs = Array.isArray(row.images) ? row.images : row.imageUrl ? [row.imageUrl] : [];
  if (imgs.length) {
    lines.push(""); lines.push("Images:");
    imgs.forEach((u, i) => lines.push(`${i + 1}. ${u}`));
  }
  return lines.join("\n");
}

export default function Belongings({ user }) {
  const { readOnly } = useAccess() || { readOnly: false };

  const [items, setItems] = useState([]);
  const { free } = useFreeLimits(user);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  // View modal
  const [viewRow, setViewRow] = useState(null);
  const viewPrintRef = useRef(null);

  // Form state
  const [form, setForm] = useState({
    title: "", description: "", estimatedValue: "",
    contactId: "", customContact: "", existingImages: [], newImages: [],
  });
  const fileInputRef = useRef(null);
  const [newImagePreviews, setNewImagePreviews] = useState([]);

  // Contacts feed
  const { contacts } = useUserContacts(user);
  const grouped = useMemo(() => groupContactsForOptions(contacts), [contacts]);

  // Add-contact modal
  const onContactCreated = (c) => setForm((f) => ({ ...f, contactId: c.id }));
  const {
    open: showContactModal,
    setOpen: setShowContactModal,
    openModal: openAddContact,
  } = useAddContactAutoSelect(contacts, onContactCreated);

  // ✅ REMOVE the read-only blocker overlay (same as Contacts)
  useEffect(() => {
    if (!readOnly || typeof document === "undefined") return;
    const selector = '[title="Read-only while managing another account"]';
    const removeMask = () => {
      document.querySelectorAll(selector).forEach((el) => { try { el.remove(); } catch {} });
    };
    removeMask();
    const mo = new MutationObserver(() => removeMask());
    try { mo.observe(document.body, { childList: true, subtree: true }); } catch {}
    return () => { try { mo.disconnect(); } catch {} };
  }, [readOnly]);

  // Load belongings
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, "belongings"), where("uid", "==", user.uid));
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const ta = a.updatedAt?.toMillis?.() || a.updatedAt?.seconds || a.createdAt?.toMillis?.() || a.createdAt?.seconds || 0;
        const tb = b.updatedAt?.toMillis?.() || b.updatedAt?.seconds || b.createdAt?.toMillis?.() || b.createdAt?.seconds || 0;
        return tb - ta;
      });
      setItems(rows);
    });
  }, [user?.uid]);

  // Preview lifecycle
  useEffect(() => {
    newImagePreviews.forEach((u) => URL.revokeObjectURL(u));
    const urls = (form.newImages || []).map((f) => URL.createObjectURL(f));
    setNewImagePreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.newImages]);

  const clearForm = () => {
    setForm({
      title: "", description: "", estimatedValue: "",
      contactId: "", customContact: "", existingImages: [], newImages: [],
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  function openAdd() {
    if (free && items.length >= 1) { setShowUpgrade(true); return; }
    setEditing(null); clearForm(); setShowForm(true);
  }
  function openEdit(row) {
    setEditing(row);
    const existing = Array.isArray(row.images) ? row.images.slice() : row.imageUrl ? [row.imageUrl] : [];
    setForm({
      title: row.title || "",
      description: row.description || "",
      estimatedValue: row.estimatedValue || "",
      contactId: row.contactId || "",
      customContact: row.contactId === "other" ? (row.customContact || row.contactName || "") : (row.customContact || ""),
      existingImages: existing,
      newImages: [],
    });
    setShowForm(true);
  }

  async function handleDelete(row) {
    if (!window.confirm("Delete this belonging?")) return false;
    try {
      await deleteDoc(doc(db, "belongings", row.id));
      const all = Array.isArray(row.images) ? row.images : row.imageUrl ? [row.imageUrl] : [];
      for (const url of all) {
        try {
          const enc = url.split("/o/")[1]?.split("?")[0] || "";
          if (enc) await deleteObject(storageRef(storage, decodeURIComponent(enc)));
        } catch {}
      }
        return true;
    } catch (e) {
      alert("Delete failed: " + e.message);
      return false;
    }
  }

  function onFilesPicked(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setForm((f) => {
      const key = (x) => `${x.name}|${x.size}|${x.lastModified}`;
      const existingKeys = new Set((f.newImages || []).map(key));
      const merged = [...(f.newImages || [])];
      files.forEach((file) => { if (!existingKeys.has(key(file))) merged.push(file); });
      return { ...f, newImages: merged };
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  }
  function removeExistingAt(i) { setForm((f) => ({ ...f, existingImages: f.existingImages.filter((_, idx) => idx !== i) })); }
  function removeNewAt(i) { setForm((f) => ({ ...f, newImages: f.newImages.filter((_, idx) => idx !== i) })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!user?.uid) return;
    try {
      const uploadedUrls = [];
      for (const file of form.newImages || []) {
        const path = `belongings/${user.uid}/${Date.now()}_${file.name}`;
        const sref = storageRef(storage, path);
        const task = uploadBytesResumable(sref, file);
        await new Promise((res, rej) => task.on("state_changed", null, rej, res));
        const url = await getDownloadURL(task.snapshot.ref);
        uploadedUrls.push(url);
      }
      const images = [...(form.existingImages || []), ...uploadedUrls];
      const imageUrl = images[0] || "";
      const contactName = form.contactId === "other" ? (form.customContact || "") : contactNameFromId(contacts, form.contactId);

      const payload = {
        uid: user.uid,
        title: form.title,
        description: form.description,
        estimatedValue: form.estimatedValue,
        contactId: form.contactId,
        customContact: form.contactId === "other" ? form.customContact : "",
        contactName,
        imageUrl,
        images,
        updatedAt: serverTimestamp(),
      };

      const originallyExisting = Array.isArray(editing?.images) ? editing.images : editing?.imageUrl ? [editing.imageUrl] : [];
      const removed = originallyExisting.filter((u) => !payload.images.includes(u));

      if (editing) {
        await updateDoc(doc(db, "belongings", editing.id), payload);
        for (const url of removed) {
          try {
            const enc = url.split("/o/")[1]?.split("?")[0] || "";
            if (enc) await deleteObject(storageRef(storage, decodeURIComponent(enc)));
          } catch {}
        }
      } else {
        await addDoc(collection(db, "belongings"), { ...payload, createdAt: serverTimestamp() });
      }

      setShowForm(false); setEditing(null); clearForm();
    } catch (e) {
      alert("Save failed: " + e.message);
    }
  }

  // Group items per contact
  const groups = useMemo(() => {
    const map = new Map();
    items.forEach((b) => {
      const name =
        b.contactName ||
        (b.contactId && contactNameFromId(contacts, b.contactId)) ||
        b.beneficiary ||
        b.assignedTo ||
        "Unassigned";
      if (!map.has(name)) map.set(name, []);
      map.get(name).push(b);
    });
    return Array.from(map.entries())
      .filter(([, rows]) => rows.length > 0)
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [items, contacts]);

  // Header actions (manager-clickable)
  const ActionsDesktop = () => (
    <div className="dp-help-row" data-allow-readonly="1">
      <button type="button" className="btn dp-help read" onMouseDown={(e)=>e.stopPropagation()} onClick={() => setShowDrawer(true)} data-allow-readonly="1">
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button type="button" className="btn dp-help see" onMouseDown={(e)=>e.stopPropagation()} onClick={() => setShowVideo(true)} data-allow-readonly="1">
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </div>
  );
  const ActionsMobile = ActionsDesktop;

  // View actions (standardized)
  function viewPrint() {
    if (!viewRow) return;
    printEntry(viewRow, {
      title: viewRow?.title ? `Belonging — ${viewRow.title}` : "Belonging",
      imageKey: "imageUrl",
      galleryKey: "images",
    });
  }
  function viewDownload() {
    if (!viewRow) return;
    const filename = `belonging-${(viewRow?.title || "entry").replace(/\s+/g, "-").toLowerCase()}.html`;
    downloadEntry(viewRow, {
      filename,
      title: viewRow?.title ? `Belonging — ${viewRow.title}` : "Belonging",
      imageKey: "imageUrl",
      galleryKey: "images",
    });
  }
  function exportTxt() {
    if (!viewRow) return;
    const txt = buildSummaryText(viewRow, contacts);
    saveTextFile("belonging.txt", txt);
  }
  function forwardByEmail() {
    if (!viewRow) return;
    const subject = "Belonging details";
    const body = buildSummaryText(viewRow, contacts);
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  return (
    <div className="container belongings-page" data-allow-readonly="1">
      {/* Header */}
      <div className="header-row">
        <h2>Belongings</h2>
        <div className="header-actions desktop-only"><ActionsDesktop /></div>
        {!readOnly && <button className="btn-add" onClick={openAdd}>+</button>}
      </div>

      {/* Hero */}
      <div className="hero-wrap">
        <img src="/lastwish-account-belongings.jpg" alt="Belongings" className="hero-img" />
      </div>
      <div className="mobile-only" style={{ margin: "10px 0" }}>
        <ActionsMobile />
      </div>

      {/* Grouped sections */}
      {groups.length === 0 ? (
        <div className="empty-state">No belongings added yet.</div>
      ) : (
        <div className="bel-sections" data-allow-readonly="1">
          {groups.map(([contactName, rows]) => (
            <section className="bel-section" key={contactName}>
              <div className="bel-section-title">{contactName}</div>
              <div className="dv-list" data-allow-readonly="1">
                {rows.map((b) => (
                  <div
                    key={b.id}
                    className="card dv-item"
                    data-allow-readonly="1"
                    onClick={() => setViewRow(b)}
                  >
                    <div className="dv-left">
                      {b.imageUrl ? (
                        <img src={b.imageUrl} alt={b.title || ""} className="tile-thumb" />
                      ) : (
                        <div
                          className="tile-thumb placeholder"
                          aria-hidden="true"
                          style={{ display: "grid", placeItems: "center", background: "#f3f3f3" }}
                        >
                          🎁
                        </div>
                      )}
                    </div>
                    <div className="dv-main">
                      <div className="tile-title">{b.title || "(Untitled)"}</div>
                      {b.description && <div className="tile-sub">{b.description}</div>}
                      {b.estimatedValue && (
                        <div className="tile-sub">Estimated value: {b.estimatedValue}</div>
                      )}
                    </div>
                    <div className="dv-actions" data-allow-readonly="1">
                      {readOnly && (
                        <button
                          type="button"
                          className="btn btn-main btn-edit"
                          aria-label="View"
                          data-allow-readonly="1"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); setViewRow(b); }}
                        >
                          View
                        </button>
                      )}

                      {!readOnly && (
                       <button
                          className="btn btn-main btn-edit"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(b);
                          }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Form modal (owner only) */}
      {showForm && (
        <div className="bel-modal-bg" onClick={() => setShowForm(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <h3>{editing ? "Edit Belonging" : "Add Belonging"}</h3>
            <form className="modal-body" onSubmit={handleSubmit}>
              <label>Title</label>
              <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />

              <label>Description</label>
              <textarea className="input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

              <label>Estimated Value</label>
              <input className="input" value={form.estimatedValue} onChange={(e) => setForm({ ...form, estimatedValue: e.target.value })} placeholder="e.g., €250" />

              <label>Assign To</label>
              <select
                className="input"
                value={form.contactId}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "__ADD__") { openAddContact(); return; }
                  setForm({ ...form, contactId: v });
                }}
              >
                <option value="">Select contact…</option>
                <option value="__ADD__">+ Add New Contact…</option>
                {grouped.map((g) =>
                  g.items.length ? (
                    <optgroup key={g.key} label={g.label}>
                      {g.items.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </optgroup>
                  ) : null
                )}
                <option value="other">Other (custom)</option>
              </select>

              {form.contactId === "other" && (
                <>
                  <label>Custom Contact</label>
                  <input className="input" value={form.customContact} onChange={(e) => setForm({ ...form, customContact: e.target.value })} placeholder="Name of beneficiary" />
                </>
              )}

              <label>Photos</label>
              <input type="file" accept="image/*" multiple ref={fileInputRef} onChange={onFilesPicked} />

              {(form.existingImages.length > 0 || newImagePreviews.length > 0) && (
                <div className="bel-images-grid">
                  {form.existingImages.map((u, i) => (
                    <div key={`ex-${i}`} className="thumb">
                      <img src={u} alt="" />
                      <button type="button" className="thumb-x" onClick={() => removeExistingAt(i)} aria-label="Remove image">×</button>
                    </div>
                  ))}
                  {newImagePreviews.map((u, i) => (
                    <div key={`nw-${i}`} className="thumb">
                      <img src={u} alt="" />
                      <button type="button" className="thumb-x" onClick={() => removeNewAt(i)} aria-label="Remove image">×</button>
                    </div>
                  ))}
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn btn-cancel" onClick={() => setShowForm(false)}>Cancel</button>
                {editing && !readOnly && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={async (e) => {
                      e.preventDefault();
                      const ok = await handleDelete(editing);
                      if (!ok) return;
                      setShowForm(false);
                      setEditing(null);
                      clearForm();
                    }}
                  >
                    Delete
                  </button>
                )}
                <button type="submit" className="btn btn-main">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View modal (summary + thumbnails) — now portaled to <body> so it sits above footer */}
      {viewRow && createPortal(
        <div
          className="bel-modal-bg"
          onClick={() => setViewRow(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2147483647, // ✅ highest wins over managed footer
          }}
        >
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <h3>View Belonging</h3>
            <div className="modal-body" style={{ gap: 6 }}>
              <div><b>Title:</b> {viewRow.title || "—"}</div>
              <div><b>Description:</b> {viewRow.description || "—"}</div>
              <div><b>Estimated Value:</b> {viewRow.estimatedValue || "—"}</div>
              <div>
                <b>Assign To:</b>{" "}
                {viewRow.contactId === "other"
                  ? viewRow.customContact || viewRow.contactName || "—"
                  : viewRow.contactName ||
                    (viewRow.contactId ? contactNameFromId(contacts, viewRow.contactId) : "—")}
              </div>

              {((Array.isArray(viewRow.images) && viewRow.images.length) || viewRow.imageUrl) && (
                <>
                  <div className="ml-view-sub" style={{ marginTop: 8 }}>Images</div>
                  <div className="ml-view-grid">
                    {(Array.isArray(viewRow.images) && viewRow.images.length ? viewRow.images : [viewRow.imageUrl])
                      .filter(Boolean)
                      .map((u, i) => (
                        <a key={`img-${i}`} href={u} target="_blank" rel="noreferrer" className="ml-view-thumb-link" title="Open image" data-allow-readonly="1">
                          <img src={u} alt="" className="ml-view-thumb" />
                        </a>
                      ))}
                  </div>
                </>
              )}
            </div>

            {/* Footer actions (manager-safe) */}
            <div className="form-actions" style={{ marginTop: 12 }} data-allow-readonly="1">
              <button className="btn btn-main" type="button" onClick={viewPrint} data-allow-readonly="1">
                Print
              </button>
              <button className="btn" type="button" onClick={viewDownload} data-allow-readonly="1">
                Download
              </button>
              <button className="btn" type="button" onClick={exportTxt} data-allow-readonly="1">
                Export
              </button>

              
              <button className="btn" type="button" onClick={forwardByEmail} data-allow-readonly="1">
                Forward
              </button>

              {!readOnly && (
                <button
                  className="btn btn-danger"
                  type="button"
                  onClick={async () => {
                    const ok = await handleDelete(viewRow);
                    if (!ok) return;
                    setViewRow(null);
                  }}
                >
                  Delete
                </button>
              )}

              <button className="btn btn-cancel" type="button" onClick={() => setViewRow(null)} data-allow-readonly="1">
                Close
              </button>
            </div>
          </div>

          {/* Hidden print root */}
          <div style={{ position: "fixed", left: "-9999px", top: 0, opacity: 0, display: "none" }} aria-hidden>
            <div ref={viewPrintRef} style={{ width: 740, padding: 8 }}>
              <h2 style={{ margin: 0, padding: 0 }}>Belonging</h2>
              <p style={{ color: "#657899" }}>Generated: {new Date().toLocaleString()}</p>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12pt" }}>
                <tbody>
                  <tr><td style={{ padding: "6px 8px", fontWeight: 700 }}>Title</td><td style={{ padding: "6px 8px" }}>{viewRow.title || "—"}</td></tr>
                  <tr><td style={{ padding: "6px 8px", fontWeight: 700 }}>Description</td><td style={{ padding: "6px 8px" }}>{viewRow.description || "—"}</td></tr>
                  <tr><td style={{ padding: "6px 8px", fontWeight: 700 }}>Estimated Value</td><td style={{ padding: "6px 8px" }}>{viewRow.estimatedValue || "—"}</td></tr>
                  <tr><td style={{ padding: "6px 8px", fontWeight: 700 }}>Assign To</td><td style={{ padding: "6px 8px" }}>
                    {viewRow.contactId === "other"
                      ? viewRow.customContact || viewRow.contactName || "—"
                      : viewRow.contactName ||
                        (viewRow.contactId ? contactNameFromId(contacts, viewRow.contactId) : "—")}
                  </td></tr>
                </tbody>
              </table>

              {((Array.isArray(viewRow.images) && viewRow.images.length) || viewRow.imageUrl) && (
                <>
                  <h3 style={{ marginTop: 14 }}>Images</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                    {(Array.isArray(viewRow.images) && viewRow.images.length ? viewRow.images : [viewRow.imageUrl])
                      .filter(Boolean)
                      .map((u, i) => (
                        <img key={`pimg-${i}`} src={u} alt="" style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 8, border: "1px solid #eee" }} />
                      ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add-Contact modal */}
      <AddContactModal open={showContactModal} onClose={() => setShowContactModal(false)} user={user} />

      {/* Video player */}
      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://youtu.be/0CS6z4tQ8Ts?si=TnUEWXKKuGnLAh-R"
        title="How the Belongings page works"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {/* Drawer */}
      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">How the Belongings page works</div>
              <div className="dp-drawer-tools">
                <button className="btn btn-main" onClick={() => window.print()} type="button" data-allow-readonly="1">
                  Print
                </button>
                <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button" data-allow-readonly="1">
                  Close
                </button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <p>Catalogue items you own and assign them to the right person. Add a photo, description, and estimated value. Items appear grouped by beneficiary for clarity.</p>
              <ol>
                <li>Use <b>+</b> to add a belonging and complete the details.</li>
                <li>Pick a contact, or choose <i>Other</i> to type a custom name.</li>
                <li>Edit or delete items any time to keep your list accurate.</li>
              </ol>
              <p><i>Tip:</i> Clear, specific descriptions avoid confusion later.</p>
            </div>
          </aside>
        </div>
      )}

      {/* Styles (unchanged look) */}
      <style>{`
        .header-row{display:flex;align-items:center;justify-content:space-between;gap:12px}
        .header-actions{display:flex;align-items:center;gap:10px}
        .desktop-only{display:none} .mobile-only{display:block}
        @media(min-width:900px){.desktop-only{display:block}.mobile-only{display:none}}

        .hero-wrap{margin:6px 0 14px}
        .hero-img{width:100%;height:130px;object-fit:cover;border-radius:14px}
        @media (max-width: 700px){
          .hero-img{height:110px}
        }

        .dp-help-row{display:flex;gap:10px;flex-wrap:wrap}
        .dp-help{border-radius:8px;padding:10px 14px;display:inline-flex;align-items:center;gap:10px;font-weight:700;color:#fff;border:none}
        .dp-help.read{background:#650202;color:#fff}
        .dp-help.see{background:#003353;color:#fff}
        .dp-help-ico{font-size:1.05rem}

        .dp-drawer-bg{position:fixed;inset:0;background:rgba(0,0,0,0.3);z-index:10000;display:flex;justify-content:flex-end}
        .dp-drawer{position:fixed;top:56px;right:0;height:calc(100vh - 56px);width:min(520px,95vw);background:#fff;box-shadow:-8px 0 24px rgba(0,0,0,0.15);border-top-left-radius:12px;border-bottom-left-radius:12px;display:flex;flex-direction:column;overflow:hidden}
        .dp-drawer-head{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:14px 16px;border-bottom:1px solid #f0f0f0;background:#fafafa}
        .dp-drawer-title{font-weight:700;color:#2a0516}
        .dp-drawer-tools{display:flex;gap:8px}
        .dp-drawer-body{padding:16px;overflow:auto}

        .bel-sections{display:grid;gap:18px}
        .bel-section-title{font-weight:800;color:#2a0516;opacity:.9;font-size:14px;margin-left:6px}
        .dv-list{display:grid;grid-template-columns:1fr;gap:16px}
        .dv-item{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:12px;padding:12px}
        .dv-left{width:42px}
        .dv-main{min-width:0}
        .dv-main .tile-title{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .dv-actions{display:flex;gap:8px;align-items:center;justify-self:end}
        .tile-thumb.placeholder{color:#8f9bb3;font-size:1rem}
        @media(max-width:700px){
          .dv-main .tile-title{font-size:.98rem}
          .dv-main .tile-sub{font-size:.86rem}
        }

        .bel-images-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(90px,1fr));gap:10px;margin-top:8px}
        .bel-images-grid .thumb{position:relative;width:100%;aspect-ratio:1/1;border-radius:10px;overflow:hidden;border:1px solid #e5e7eb;background:#f8fafc}
        .bel-images-grid . thumb img{width:100%;height:100%;object-fit:cover;display:block}

        .bel-images-grid .thumb .thumb-x{position:absolute;top:6px;right:6px;width:24px;height:24px;border-radius:50%;border:none;background:rgba(0,0,0,.6);color:#fff;font-weight:900;line-height:24px;text-align:center;cursor:pointer}

        .bel-modal-bg{position:fixed;inset:0;background:rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;z-index:30000}
        .modal-card{background:#fff;border-radius:12px;padding:20px;max-width:560px;width:95vw;box-shadow:0 10px 30px rgba(0,0,0,.15)}
        .modal-body{display:flex;flex-direction:column}
        .belongings-page .modal-bg{z-index:30000;background:rgba(0,0,0,.3)}

        .form-actions{display:flex;gap:10px;margin-top:12px;flex-wrap:wrap}
      

        .ml-view-sub{font-weight:800}
        .ml-view-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(110px,1fr));gap:10px}
        .ml-view-thumb{width:100%;height:78px;object-fit:cover;border-radius:8px;border:1px solid #e5e7eb;background:#f7f7fb}
      `}</style>
      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} feature="documents" />
    </div>
  );
}
